// ============================================================================
// 📁 FILE: src/modules/ticket/hooks/useTicketDetail.js
// 🎯 PURPOSE:
//    Unified data-loading hook for the Ticket Detail Page.
//    Loads ticket, comments, attachments, activity log.
//    Exposes actions: addComment, changeStatus, assign, uploadAttachment.
//
// 📌 WORKS WITH:
//    - ticketDetailService.js (API wrapper)
//    - TicketDetailPage.jsx
//
// 🧩 RETURNS:
//    {
//      ticket, comments, attachments, audits,
//      loading, error,
//      addComment, changeStatus, assign, uploadAttachment,
//      refreshAll(), reload()
//    }
//
// ============================================================================

import { useState, useEffect, useCallback } from "react";
import ticketDetailService from "../services/ticketDetailService";

export default function useTicketDetail(ticketId, initialLang = "en") {
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [lang] = useState(initialLang);

  // -------------------------------------------------------------------------
  // 🔵 LOAD TICKET
  // -------------------------------------------------------------------------
  const loadTicket = useCallback(async () => {
    if (!ticketId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await ticketDetailService.getTicket(ticketId, { language: lang });

      if (res?.success) {
        setTicket(res.data);
      } else {
        setError(res?.message || "Failed to load ticket");
      }
    } catch (e) {
      setError(e.message || "Failed to load ticket");
    } finally {
      setLoading(false);
    }
  }, [ticketId, lang]);

  // -------------------------------------------------------------------------
  // 💬 LOAD COMMENTS
  // -------------------------------------------------------------------------
  const loadComments = useCallback(async () => {
    try {
      const res = await ticketDetailService.getComments(ticketId);
      if (res?.success) {
        setComments(res.data.items || []);
      }
    } catch (e) {
      console.warn("Failed to load comments");
    }
  }, [ticketId]);

  // -------------------------------------------------------------------------
  // 📎 LOAD ATTACHMENTS
  // -------------------------------------------------------------------------
  const loadAttachments = useCallback(async () => {
    try {
      const res = await ticketDetailService.getAttachments(ticketId);
      if (res?.items) {
        setAttachments(res.items);
      }
    } catch (e) {
      console.warn("Failed to load attachments");
    }
  }, [ticketId]);

  // -------------------------------------------------------------------------
  // 📜 LOAD ACTIVITY LOG
  // -------------------------------------------------------------------------
  const loadAudits = useCallback(async () => {
    try {
      const res = await ticketDetailService.getAudit(ticketId);
      if (res?.items) {
        setAudits(res.items);
      }
    } catch (e) {
      console.warn("Failed to load audits");
    }
  }, [ticketId]);

  // -------------------------------------------------------------------------
  // 🔵 INITIAL LOAD
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!ticketId) return;
    (async () => {
      await loadTicket();
      await Promise.all([loadComments(), loadAttachments(), loadAudits()]);
    })();
  }, [ticketId, loadTicket, loadComments, loadAttachments, loadAudits]);

  // -------------------------------------------------------------------------
  // 🟢 ACTION: ADD COMMENT
  // -------------------------------------------------------------------------
  const addComment = async (payload) => {
    const res = await ticketDetailService.postComment(ticketId, payload);
    if (res?.success) {
      await loadComments();
      await loadAudits();
    }
    return res;
  };

  // -------------------------------------------------------------------------
  // 🟡 ACTION: CHANGE STATUS
  // -------------------------------------------------------------------------
  const changeStatus = async (payload) => {
    const res = await ticketDetailService.setStatus(ticketId, payload);
    if (res?.success) {
      await loadTicket();
      await loadAudits();
    }
    return res;
  };

  // -------------------------------------------------------------------------
  // 🟠 ACTION: ASSIGN TICKET
  // -------------------------------------------------------------------------
  const assign = async (payload) => {
    const res = await ticketDetailService.assignTicket(ticketId, payload);
    if (res?.success) {
      await loadTicket();
      await loadAudits();
    }
    return res;
  };

  // -------------------------------------------------------------------------
  // 📎 ACTION: UPLOAD ATTACHMENT
  // -------------------------------------------------------------------------
  const uploadAttachment = async (formData) => {
    const res = await ticketDetailService.uploadAttachment(ticketId, formData);
    if (res?.success) {
      await loadAttachments();
      await loadAudits();
    }
    return res;
  };

  // -------------------------------------------------------------------------
  // 🔄 FULL REFRESH
  // -------------------------------------------------------------------------
  const refreshAll = async () => {
    await loadTicket();
    await loadComments();
    await loadAttachments();
    await loadAudits();
  };

  return {
    ticket,
    comments,
    attachments,
    audits,
    loading,
    error,
    reload: loadTicket,
    addComment,
    changeStatus,
    assign,
    uploadAttachment,
    refreshAll,
  };
}
